import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { LogOut, Users, KeyRound } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { applicationsApi, adminApi, auth } from '../utils/api';
import { ChangePasswordDialog } from '../components/ChangePasswordDialog';
import { AdminUsersDialog } from '../components/AdminUsersDialog';
import { StatsCards } from '../components/admin/StatsCards';
import { ApplicationsTable } from '../components/admin/ApplicationsTable';
import { ApplicationDetailDialog } from '../components/admin/ApplicationDetailDialog';
import { getPresetRange, parseLocalDate } from '../components/admin/dateRange';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { SEO } from '../components/SEO';

// Dev-only error logging — silent in production to avoid leaking internals.
const devError = (msg, err) => {
  if (process.env.NODE_ENV !== 'production') {
    console.error(msg, err);
  }
};

export const AdminDashboardPage = () => {
  const [applications, setApplications] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, inProgress: 0, completed: 0 });
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [verified, setVerified] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [inquiryType, setInquiryType] = useState('credit');
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [usersDialogOpen, setUsersDialogOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRows, setTotalRows] = useState(0);
  const [selectedIds, setSelectedIds] = useState([]);
  const [tabCounts, setTabCounts] = useState({ credit: 0, contact: 0 });
  const [datePreset, setDatePreset] = useState('all');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const navigate = useNavigate();
  const { toast } = useToast();

  // Fetch new-lead counts for BOTH inquiry types so we can show a red dot
  // on tab headers whenever a new (unread) application/message exists.
  const fetchTabCounts = useCallback(async () => {
    try {
      const [credit, contact] = await Promise.all([
        applicationsApi.getAll({ inquiryType: 'credit', pageSize: 1 }),
        applicationsApi.getAll({ inquiryType: 'contact', pageSize: 1 }),
      ]);
      setTabCounts({
        credit: credit.data?.stats?.new || 0,
        contact: contact.data?.stats?.new || 0,
      });
    } catch (error) {
      devError('Failed to load tab counts:', error);
    }
  }, []);

  // Resolve the current date-range filter into ISO-8601 strings for the API.
  const resolveDateRange = useCallback(() => {
    if (datePreset === 'all') return { dateFrom: null, dateTo: null };
    if (datePreset === 'custom') {
      const from = parseLocalDate(customFrom, false);
      const to = parseLocalDate(customTo, true);
      return {
        dateFrom: from ? from.toISOString() : null,
        dateTo: to ? to.toISOString() : null,
      };
    }
    const range = getPresetRange(datePreset);
    return {
      dateFrom: range?.from ? range.from.toISOString() : null,
      dateTo: range?.to ? range.to.toISOString() : null,
    };
  }, [datePreset, customFrom, customTo]);

  const fetchApplications = useCallback(async () => {
    setLoading(true);
    try {
      const { dateFrom, dateTo } = resolveDateRange();
      const filters = { page, pageSize, inquiryType };
      if (statusFilter !== 'all') filters.status = statusFilter;
      if (dateFrom) filters.dateFrom = dateFrom;
      if (dateTo) filters.dateTo = dateTo;

      const response = await applicationsApi.getAll(filters);
      setApplications(response.data.applications);
      setStats(response.data.stats);
      setTotalPages(response.data.totalPages || 1);
      setTotalRows(response.data.total || 0);
    } catch (error) {
      if (error.response?.status === 401) return;
      devError('Failed to load applications:', error);
      toast({ title: "Xəta", description: "Müraciətlər yüklənə bilmədi", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, [statusFilter, inquiryType, page, pageSize, resolveDateRange, toast]);

  useEffect(() => {
    if (!auth.isAuthenticated()) {
      navigate('/ascorpadmin/entry');
      return;
    }
    adminApi.verify()
      .then(() => setVerified(true))
      .catch(() => navigate('/ascorpadmin/entry'));
  }, [navigate]);

  useEffect(() => {
    if (verified) fetchApplications();
  }, [verified, fetchApplications]);

  // Refresh tab counts on mount and every 60s so new leads light up the red dot.
  useEffect(() => {
    if (!verified) return;
    fetchTabCounts();
    const id = setInterval(fetchTabCounts, 60000);
    return () => clearInterval(id);
  }, [verified, fetchTabCounts]);

  // Also refresh counts after each fetchApplications (mutations trigger it via fetchApplications).
  useEffect(() => {
    if (verified) fetchTabCounts();
    // Depend on applications length + stats.new so counts refresh after status changes / deletes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applications.length, stats?.new, verified]);

  const handleLogout = async () => {
    try {
      await adminApi.logout();
    } catch (error) {
      devError('Logout API call failed (clearing token anyway):', error);
    }
    auth.removeToken();
    toast({ title: "Çıxış uğurlu", description: "Admin panelindən çıxış etdiniz" });
    navigate('/ascorpadmin/entry');
  };

  const handleStatusChange = async (id, newStatus) => {
    setUpdatingStatus(true);
    try {
      await applicationsApi.updateStatus(id, newStatus);
      setApplications(applications.map(app =>
        app.id === id ? { ...app, status: newStatus } : app
      ));
      if (selectedApplication?.id === id) {
        setSelectedApplication({ ...selectedApplication, status: newStatus });
      }
      await fetchApplications();
      toast({ title: "Status yeniləndi", description: `Müraciətin statusu "${newStatus}" olaraq dəyişdirildi` });
    } catch (error) {
      devError('Status update failed:', error);
      toast({ title: "Xəta", description: "Status yenilənə bilmədi", variant: "destructive" });
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu müraciəti həmişəlik silmək istədiyinizə əminsiniz?')) return;
    try {
      await applicationsApi.delete(id);
      // Optimistic UI: remove locally, then refresh authoritative stats/page.
      setApplications((prev) => prev.filter((a) => a.id !== id));
      setSelectedIds((prev) => prev.filter((sid) => sid !== id));
      if (selectedApplication?.id === id) setSelectedApplication(null);
      await fetchApplications();
      toast({ title: "Müraciət silindi", description: "Sətir siyahıdan çıxarıldı" });
    } catch (error) {
      devError('Delete failed:', error);
      toast({ title: "Xəta", description: "Müraciət silinə bilmədi", variant: "destructive" });
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Seçilmiş ${selectedIds.length} müraciəti həmişəlik silmək istədiyinizə əminsiniz?`)) return;
    try {
      const response = await applicationsApi.bulkDelete(selectedIds);
      const deleted = response.data?.deletedCount ?? selectedIds.length;
      setSelectedIds([]);
      await fetchApplications();
      toast({ title: "Toplu silmə uğurlu", description: `${deleted} müraciət silindi` });
    } catch (error) {
      devError('Bulk delete failed:', error);
      toast({ title: "Xəta", description: "Müraciətlər silinə bilmədi", variant: "destructive" });
    }
  };

  const handleBulkStatus = async (newStatus) => {
    if (selectedIds.length === 0 || !newStatus) return;
    if (!window.confirm(`Seçilmiş ${selectedIds.length} müraciətin statusunu "${newStatus}" olaraq yeniləmək istədiyinizə əminsiniz?`)) return;
    try {
      const response = await applicationsApi.bulkUpdateStatus(selectedIds, newStatus);
      const updated = response.data?.updatedCount ?? selectedIds.length;
      setSelectedIds([]);
      await fetchApplications();
      toast({ title: "Statuslar yeniləndi", description: `${updated} müraciət "${newStatus}" statusuna keçirildi` });
    } catch (error) {
      devError('Bulk status update failed:', error);
      toast({ title: "Xəta", description: "Statuslar yenilənə bilmədi", variant: "destructive" });
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const toggleSelectAll = () => {
    const pageIds = applications.map((a) => a.id);
    const allSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.includes(id));
    if (allSelected) {
      setSelectedIds((prev) => prev.filter((id) => !pageIds.includes(id)));
    } else {
      setSelectedIds((prev) => Array.from(new Set([...prev, ...pageIds])));
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const { dateFrom, dateTo } = resolveDateRange();
      const filters = { inquiryType };
      if (statusFilter !== 'all') filters.status = statusFilter;
      if (dateFrom) filters.dateFrom = dateFrom;
      if (dateTo) filters.dateTo = dateTo;

      const response = await applicationsApi.exportExcel(filters);
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      // Extract filename from Content-Disposition if present
      const disposition = response.headers?.['content-disposition'] || '';
      const match = disposition.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : `muraciyetler_${new Date().toISOString().slice(0, 10)}.xlsx`;

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast({ title: "İxrac uğurlu", description: `${applications.length} müraciət yükləndi` });
    } catch (error) {
      devError('Excel export failed:', error);
      toast({ title: "Xəta", description: "Excel ixracı alınmadı", variant: "destructive" });
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO title="Admin Panel" description="İDK BOKT admin idarə panel" path="/ascorpadmin/dashboard" noindex />
      <header className="bg-white shadow-sm border-b">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <img src="/logo-idk.png" alt="İDK" className="h-8 w-auto" />
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Admin Panel</h1>
                <p className="text-xs sm:text-sm text-gray-600">Müraciətlərin idarə edilməsi</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button onClick={() => setUsersDialogOpen(true)} variant="outline" data-testid="manage-users-btn">
                <Users className="mr-2 h-4 w-4" />
                <span className="hidden sm:inline">İstifadəçilər</span>
              </Button>
              <Button onClick={() => setPasswordDialogOpen(true)} variant="outline" data-testid="change-password-btn">
                <KeyRound className="mr-2 h-4 w-4" />
                <span className="hidden sm:inline">Parolu dəyiş</span>
              </Button>
              <Button
                onClick={handleLogout}
                variant="outline"
                className="border-red-300 text-red-700 hover:bg-red-50"
                data-testid="admin-logout-btn"
              >
                <LogOut className="mr-2 h-4 w-4" />
                <span className="hidden sm:inline">Çıxış</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
        <Tabs
          value={inquiryType}
          onValueChange={(v) => {
            setInquiryType(v);
            setPage(1);
            setSelectedIds([]);
            setStatusFilter('all');
          }}
          className="mb-6"
        >
          <TabsList className="h-10 bg-gray-100 p-1" data-testid="inquiry-type-tabs">
            <TabsTrigger value="credit" className="relative px-5 h-8 text-sm" data-testid="tab-credit">
              Kredit müraciətləri
              {tabCounts.credit > 0 && (
                <span
                  className="absolute -top-0.5 -right-0.5 flex h-2.5 w-2.5"
                  aria-label={`${tabCounts.credit} yeni müraciət var`}
                  data-testid="tab-credit-dot"
                >
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-600"></span>
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="contact" className="relative px-5 h-8 text-sm" data-testid="tab-contact">
              Əlaqə mesajları
              {tabCounts.contact > 0 && (
                <span
                  className="absolute -top-0.5 -right-0.5 flex h-2.5 w-2.5"
                  aria-label={`${tabCounts.contact} yeni mesaj var`}
                  data-testid="tab-contact-dot"
                >
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-600"></span>
                </span>
              )}
            </TabsTrigger>
          </TabsList>
        </Tabs>
        <StatsCards stats={stats} />
        <ApplicationsTable
          applications={applications}
          loading={loading}
          statusFilter={statusFilter}
          onStatusFilterChange={(v) => { setStatusFilter(v); setPage(1); setSelectedIds([]); }}
          inquiryType={inquiryType}
          onRefresh={fetchApplications}
          onSelect={setSelectedApplication}
          onStatusChange={handleStatusChange}
          onDelete={handleDelete}
          onExport={handleExport}
          exporting={exporting}
          page={page}
          totalPages={totalPages}
          totalRows={totalRows}
          onPageChange={(p) => { setPage(p); setSelectedIds([]); }}
          pageSize={pageSize}
          onPageSizeChange={(v) => { setPageSize(v); setPage(1); setSelectedIds([]); }}
          datePreset={datePreset}
          onDatePresetChange={(v) => {
            setDatePreset(v);
            if (v !== 'custom') { setCustomFrom(''); setCustomTo(''); }
            setPage(1); setSelectedIds([]);
          }}
          customFrom={customFrom}
          customTo={customTo}
          onCustomFromChange={(v) => { setCustomFrom(v); setPage(1); setSelectedIds([]); }}
          onCustomToChange={(v) => { setCustomTo(v); setPage(1); setSelectedIds([]); }}
          selectedIds={selectedIds}
          onToggleSelect={toggleSelect}
          onToggleSelectAll={toggleSelectAll}
          onBulkDelete={handleBulkDelete}
          onBulkStatus={handleBulkStatus}
        />
      </main>

      <ApplicationDetailDialog
        application={selectedApplication}
        onClose={() => setSelectedApplication(null)}
        onStatusChange={handleStatusChange}
        updatingStatus={updatingStatus}
      />
      <ChangePasswordDialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen} />
      <AdminUsersDialog open={usersDialogOpen} onOpenChange={setUsersDialogOpen} />
    </div>
  );
};
